<template>
  <div>
    <h1 class="text-xl font-medium leading-6 text-gray-900 mr-2">Welcome to Humaniz!</h1>
    <p class="mt-1 text-sm text-gray-500">Let's get your account setup and ready to go.</p>

    <div class="mt-6 mb-6 grid grid-cols-1 gap-x-4 gap-y-2 max-w-md mx-auto">
      <TextInput
        label="Company Name"
        placeholder="Company Name"
        :model-value="form.team_name"
        class="w-full"
        @update:model-value="value => (form.team_name = value)"
      />
      <TextInput
        label="Email"
        placeholder="Email"
        type="email"
        :model-value="form.email"
        class="w-full"
        @update:model-value="value => (form.email = value)"
      />
      <FormKit
        v-model="password"
        type="password"
        name="password"
        label="Password"
        placeholder="Password"
        validation="required|length:6"
        :validation-messages="{
          required: 'Password is required',
          length: 'Password must be at least 6 characters',
        }"
        validation-behavior="live"
        :validation-rules="{
          matchConfirmation: password =>
            !password_confirmation.value || password === password_confirmation.value || 'Passwords do not match',
        }"
        class="w-full"
      />
      <FormKit
        v-model="password_confirmation"
        type="password"
        name="password_confirmation"
        label="Password Confirmation"
        placeholder="Confirm Password"
        validation="required"
        :validation-messages="{
          required: 'Password confirmation is required',
        }"
        validation-behavior="live"
        :validation-rules="{
          matches: confirmation => confirmation === password.value || 'Passwords do not match',
        }"
        class="w-full"
      />
    </div>

    <div class="py-3 flex justify-center">
      <span class="flex w-full rounded-md shadow-sm sm:w-auto">
        <TButton
          color="amber"
          :label="isLoading ? 'Creating Account...' : 'Continue'"
          :disabled="!isPasswordValid || isLoading"
          @click="handleContinue"
        />
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import TButton from '../../shared/TButton.vue';
import TextInput from '../../shared/TextInput.vue';
import { useInviteDataStore } from '@/stores/inviteData.ts';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';

const inviteDataStore = useInviteDataStore();
const loadingSpinnerStore = useLoadingSpinnerStore();
const route = useRoute();
const router = useRouter();
const emit = defineEmits(['register']);

const form = reactive({
  team_name: '',
  email: '',
});

const password = ref('');
const password_confirmation = ref('');

const isLoading = computed(() => loadingSpinnerStore.loadingSpinner);

const isPasswordValid = computed(() => {
  return (
    password.value !== '' &&
    password_confirmation.value !== '' &&
    password.value === password_confirmation.value &&
    password.value.length >= 6 &&
    !document.querySelector('.formkit-message') // Ensure no validation errors are showing
  );
});

function resetPasswordFields() {
  password.value = '';
  password_confirmation.value = '';
}

async function fetchInviteData() {
  const inviteToken = route.params.invite_token;
  const result = await inviteDataStore.fetchInviteData({ inviteToken });

  if (result.status === 'success' && result.record) {
    form.team_name = result.record.team_name || '';
    form.email = result.record.contact_email || '';
    resetPasswordFields();
  } else {
    // Redirect to root path if invite is invalid
    router.push('/');
  }
}

function handleContinue() {
  const inviteToken = route.params.invite_token;
  emit('register', {
    invite_token: inviteToken,
    team_name: form.team_name,
    email: form.email,
    password: password.value,
  });
}

onMounted(async () => {
  resetPasswordFields();
  await fetchInviteData();
});
</script>
